import { CsvTemplate, CsvTemplateProperty } from '@/types'

const escapeCell = (value: string): string => (/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)

const formatExample = (example: unknown): string => {
  if (example === null || example === undefined) return ''
  if (typeof example === 'string') return example
  if (typeof example === 'number' || typeof example === 'boolean') return String(example)
  return JSON.stringify(example)
}

const columnNames = (template: CsvTemplate): string[] => Object.keys(template.schema.properties ?? {})

const exampleCells = (template: CsvTemplate): string[] => {
  const properties = template.schema.properties ?? {}
  return columnNames(template).map((name) => formatExample(properties[name]?.example))
}

export const buildHeaderRow = (template: CsvTemplate): string => columnNames(template).map(escapeCell).join(',')

export const buildExampleRow = (template: CsvTemplate): string => exampleCells(template).map(escapeCell).join(',')

export const buildCsv = (template: CsvTemplate): string => {
  const rows = [buildHeaderRow(template)]
  if (exampleCells(template).some((cell) => cell !== '')) {
    rows.push(buildExampleRow(template))
  }
  return rows.join('\n') + '\n'
}

/**
 * Hand a blob to the browser as a download.
 *
 * Extracted rather than repeated because the anchor dance below is easy to get subtly wrong — an
 * element that is never appended does not click in every browser, and an object URL that is never
 * revoked holds the whole file in memory for the life of the tab.
 *
 * It is also the only way to download something the API will not serve unauthenticated: a CSV
 * behind a bearer token cannot be an `href`, so it is fetched and saved from here instead.
 */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const downloadCsvTemplate = (template: CsvTemplate): void => {
  saveBlob(new Blob([buildCsv(template)], { type: 'text/csv;charset=utf-8;' }), `${template.id}.csv`)
}

export type CsvTemplateColumn = {
  name: string
  property: CsvTemplateProperty
  required: boolean
}

export const listColumns = (template: CsvTemplate): CsvTemplateColumn[] => {
  const properties = template.schema.properties ?? {}
  const required = new Set(template.schema.required ?? [])
  return columnNames(template).map((name) => ({
    name,
    property: properties[name] ?? {},
    required: required.has(name),
  }))
}
